import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Calculator, 
  DollarSign, 
  Percent, 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  Home,
  TrendingDown,
  Clock,
  Wallet,
  FileText,
  ChevronDown,
  AlertTriangle,
  RotateCcw,
  Info
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Only the 12 states we operate in
const US_STATES = [
  { code: "AZ", name: "Arizona" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "LA", name: "Louisiana" },
  { code: "MT", name: "Montana" },
  { code: "OH", name: "Ohio" },
  { code: "SC", name: "South Carolina" },
  { code: "TX", name: "Texas" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }
];

// Zip code prefix to state mapping
const ZIP_TO_STATE = {
  // Arizona: 850-865
  ...Object.fromEntries([...Array(16)].map((_, i) => [`${850 + i}`, "AZ"])),
  // California: 900-961
  ...Object.fromEntries([...Array(62)].map((_, i) => [`${900 + i}`, "CA"])),
  // Colorado: 800-816
  ...Object.fromEntries([...Array(17)].map((_, i) => [`${800 + i}`, "CO"])),
  // Florida: 320-349
  ...Object.fromEntries([...Array(30)].map((_, i) => [`${320 + i}`, "FL"])),
  // Georgia: 300-319, 398-399
  ...Object.fromEntries([...Array(20)].map((_, i) => [`${300 + i}`, "GA"])),
  "398": "GA", "399": "GA",
  // Louisiana: 700-714
  ...Object.fromEntries([...Array(15)].map((_, i) => [`${700 + i}`, "LA"])),
  // Montana: 590-599
  ...Object.fromEntries([...Array(10)].map((_, i) => [`${590 + i}`, "MT"])),
  // Ohio: 430-459
  ...Object.fromEntries([...Array(30)].map((_, i) => [`${430 + i}`, "OH"])),
  // South Carolina: 290-299
  ...Object.fromEntries([...Array(10)].map((_, i) => [`${290 + i}`, "SC"])),
  // Texas: 750-799, 885
  ...Object.fromEntries([...Array(50)].map((_, i) => [`${750 + i}`, "TX"])),
  "885": "TX",
  // Virginia: 220-246
  ...Object.fromEntries([...Array(27)].map((_, i) => [`${220 + i}`, "VA"])),
  // Washington: 980-994
  ...Object.fromEntries([...Array(15)].map((_, i) => [`${980 + i}`, "WA"])),
};

const getStateFromZip = (zip) => {
  if (!zip || zip.length < 3) return null;
  const prefix = zip.substring(0, 3);
  return ZIP_TO_STATE[prefix] || null;
};

const LOAN_TERMS = [
  { value: "30", label: "30 Years" },
  { value: "25", label: "25 Years" },
  { value: "20", label: "20 Years" },
  { value: "15", label: "15 Years" },
  { value: "10", label: "10 Years" }
];

// Placeholder for state-based title fees (will be provided by user)
const STATE_TITLE_FEES = {
  "AZ": 1400,
  "CA": 1800,
  "CO": 1300,
  "FL": 1500,
  "GA": 1300,
  "LA": 1300,
  "MT": 1200,
  "OH": 1300,
  "SC": 1300,
  "TX": 1500,
  "VA": 1400,
  "WA": 1500
};

// State Recording/Transfer Taxes for Refinances
// These are calculated based on loan amount and vary by state
const STATE_RECORDING_TAXES = {
  "AZ": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 50, // Flat recording fee
    description: "County recording fee"
  },
  "CA": { 
    name: "Recording Fees", 
    type: "flat", 
    amount: 95, // $75 Building Homes + ~$20 county
    description: "Building Homes Act + county fees"
  },
  "CO": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 40, // HB24-1269 flat $40 per document
    description: "Flat recording fee per document"
  },
  "FL": { 
    name: "Doc Stamp + Intangible Tax", 
    type: "percentage", 
    rate: 0.0055, // $0.35/$100 + $2/$1000 = 0.35% + 0.2% = 0.55%
    description: "Documentary Stamp ($0.35/$100) + Intangible Tax ($2/$1000)"
  },
  "GA": { 
    name: "Intangible Recording Tax", 
    type: "percentage", 
    rate: 0.003, // $1.50 per $500 = $3 per $1000 = 0.3%
    description: "Intangible tax ($1.50 per $500)"
  },
  "LA": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 175, // Flat statewide
    description: "Statewide mortgage recording fee"
  },
  "MT": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 50, // Flat county fee
    description: "County recording fee"
  },
  "OH": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 55, // $39 first 2 pages + additional
    description: "County recording fee"
  },
  "SC": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 25, // Flat $25 for mortgages
    description: "Mortgage recording fee"
  },
  "TX": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 125, // Average county fee
    description: "County recording fee (no mortgage tax)"
  },
  "VA": { 
    name: "Recordation Tax", 
    type: "percentage", 
    rate: 0.00333, // $0.25/$100 state + ~$0.083/$100 local = ~$0.333/$100
    description: "State + local recordation tax ($0.333/$100)"
  },
  "WA": { 
    name: "Recording Fee", 
    type: "flat", 
    amount: 385, // $304.50 deed of trust + $80 foreclosure prevention
    description: "Recording fee + foreclosure prevention surcharge"
  }
};

// Calculate recording/transfer tax based on state and loan amount
const calculateRecordingTax = (state, loanAmount) => {
  const taxInfo = STATE_RECORDING_TAXES[state];
  if (!taxInfo) return { amount: 0, name: "Recording Fee", description: "" };
  
  if (taxInfo.type === "flat") {
    return { 
      amount: taxInfo.amount, 
      name: taxInfo.name, 
      description: taxInfo.description 
    };
  } else {
    // Percentage-based tax
    return { 
      amount: Math.round(loanAmount * taxInfo.rate * 100) / 100, 
      name: taxInfo.name, 
      description: taxInfo.description 
    };
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Calculate remaining loan balance using amortization formula
const calculateRemainingBalance = (originalAmount, annualRate, termYears, monthsSinceOrigination) => {
  if (!originalAmount || !annualRate || !termYears || monthsSinceOrigination <= 0) return null;
  
  const monthlyRate = annualRate / 100 / 12;
  const totalPayments = termYears * 12;
  const paymentsMade = Math.min(monthsSinceOrigination, totalPayments);
  
  if (monthlyRate === 0) {
    // Simple calculation for 0% interest
    return originalAmount * (1 - paymentsMade / totalPayments);
  }
  
  // Monthly payment using standard amortization formula
  const monthlyPayment = originalAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / 
                         (Math.pow(1 + monthlyRate, totalPayments) - 1);
  
  // Remaining balance after n payments
  const remainingBalance = originalAmount * Math.pow(1 + monthlyRate, paymentsMade) - 
                          monthlyPayment * (Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate;
  
  return Math.max(0, remainingBalance);
};

import { parseLocalDate } from '../utils/dateUtils';

// Calculate days between two dates
const daysBetween = (date1, date2) => {
  const d1 = parseLocalDate(date1);
  const d2 = parseLocalDate(date2);
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Calculate months between two dates
const monthsBetween = (date1, date2) => {
  const d1 = parseLocalDate(date1);
  const d2 = parseLocalDate(date2);
  return (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
};

export default function IRRRLCalculator() {
  // Form state
  const [originalClosingDate, setOriginalClosingDate] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [originalLoanAmount, setOriginalLoanAmount] = useState("");
  const [currentLoanBalance, setCurrentLoanBalance] = useState("");
  const [balanceManuallyEdited, setBalanceManuallyEdited] = useState(false);
  const [currentInterestRate, setCurrentInterestRate] = useState("");
  const [newInterestRate, setNewInterestRate] = useState("");
  const [loanTermYears, setLoanTermYears] = useState("30");
  const [discountPoints, setDiscountPoints] = useState("0");
  const [fundingFee, setFundingFee] = useState("no");
  const [monthlyPropertyTax, setMonthlyPropertyTax] = useState("0");
  const [zipCode, setZipCode] = useState("");
  const [state, setState] = useState("");
  
  // Auto-calculated title fee based on state (can be manually overridden)
  const [titleFee, setTitleFee] = useState("");
  const [titleFeeManuallyEdited, setTitleFeeManuallyEdited] = useState(false);
  
  // Underwriting fee (can be manually adjusted)
  const [underwritingFee, setUnderwritingFee] = useState("595");
  
  // Auto-calculated recording/transfer tax based on state and loan amount
  const [recordingTax, setRecordingTax] = useState({ amount: 0, name: "Recording Fee", description: "" });
  
  // Processing fee (waivable)
  const [processingFee, setProcessingFee] = useState(695);
  const [processingFeeWaived, setProcessingFeeWaived] = useState(false);
  
  // Results state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [costBreakdownOpen, setCostBreakdownOpen] = useState(false);

  // Validation warnings
  const [warnings, setWarnings] = useState([]);

  // VA Eligibility calculations
  const daysSinceOriginalClosing = originalClosingDate && closingDate 
    ? daysBetween(originalClosingDate, closingDate) 
    : 0;
  
  const paymentsMade = originalClosingDate && closingDate 
    ? monthsBetween(originalClosingDate, closingDate) 
    : 0;

  const rateReduction = currentInterestRate && newInterestRate 
    ? (parseFloat(currentInterestRate) - parseFloat(newInterestRate)).toFixed(3) 
    : 0;

  // VA Eligibility checks
  const meetsSeasoningRequirement = daysSinceOriginalClosing >= 210;
  const meetsPaymentRequirement = paymentsMade >= 6;
  const meetsNTBRequirement = parseFloat(rateReduction) >= 0.5;

  // Update title fee when state changes (only if not manually edited)
  useEffect(() => {
    if (!titleFeeManuallyEdited && state && STATE_TITLE_FEES[state]) {
      setTitleFee(STATE_TITLE_FEES[state].toString());
    }
  }, [state, titleFeeManuallyEdited]);

  // Handle title fee manual change
  const handleTitleFeeChange = (value) => {
    setTitleFee(value);
    setTitleFeeManuallyEdited(true);
  };

  // Reset title fee to auto-calculated value
  const resetTitleFeeToAuto = () => {
    if (state && STATE_TITLE_FEES[state]) {
      setTitleFee(STATE_TITLE_FEES[state].toString());
    }
    setTitleFeeManuallyEdited(false);
  };

  // Update recording tax when state or loan balance changes
  useEffect(() => {
    if (state && currentLoanBalance) {
      const taxInfo = calculateRecordingTax(state, parseFloat(currentLoanBalance) || 0);
      setRecordingTax(taxInfo);
    }
  }, [state, currentLoanBalance]);

  // Auto-calculate current loan balance when inputs change (if not manually edited)
  useEffect(() => {
    if (!balanceManuallyEdited && originalClosingDate && closingDate && originalLoanAmount && currentInterestRate && loanTermYears) {
      const months = monthsBetween(originalClosingDate, closingDate);
      if (months > 0) {
        const calculatedBalance = calculateRemainingBalance(
          parseFloat(originalLoanAmount),
          parseFloat(currentInterestRate),
          parseInt(loanTermYears),
          months
        );
        if (calculatedBalance !== null) {
          setCurrentLoanBalance(calculatedBalance.toFixed(2));
        }
      }
    }
  }, [originalClosingDate, closingDate, originalLoanAmount, currentInterestRate, loanTermYears, balanceManuallyEdited]);

  // Handle manual balance edit
  const handleBalanceChange = (value) => {
    setCurrentLoanBalance(value);
    setBalanceManuallyEdited(true);
  };

  // Reset balance to auto-calculated
  const resetBalanceToAuto = () => {
    setBalanceManuallyEdited(false);
  };

  // Update warnings when inputs change
  useEffect(() => {
    const newWarnings = [];
    
    if (currentInterestRate && newInterestRate) {
      const currentRate = parseFloat(currentInterestRate);
      const newRate = parseFloat(newInterestRate);
      
      if (newRate >= currentRate) {
        newWarnings.push("New interest rate should be lower than current rate");
      }
      
      if (currentRate - newRate < 0.5) {
        newWarnings.push("Rate reduction less than 0.5% may not meet VA Net Tangible Benefit requirement");
      }
    }
    
    if (daysSinceOriginalClosing > 0 && daysSinceOriginalClosing < 210) {
      newWarnings.push(`Only ${daysSinceOriginalClosing} days since original closing (VA requires 210 days)`);
    }
    
    if (paymentsMade > 0 && paymentsMade < 6) {
      newWarnings.push(`Only ${paymentsMade} payments made (VA requires 6 payments)`);
    }
    
    setWarnings(newWarnings);
  }, [currentInterestRate, newInterestRate, daysSinceOriginalClosing, paymentsMade]);

  // Toggle processing fee waiver
  const toggleProcessingFeeWaiver = () => {
    setProcessingFeeWaived(!processingFeeWaived);
    setProcessingFee(processingFeeWaived ? 695 : 0);
  };

  // Clear/Reset form
  const handleClearForm = () => {
    setOriginalClosingDate("");
    setClosingDate("");
    setOriginalLoanAmount("");
    setCurrentLoanBalance("");
    setBalanceManuallyEdited(false);
    setCurrentInterestRate("");
    setNewInterestRate("");
    setLoanTermYears("30");
    setDiscountPoints("0");
    setFundingFee("no");
    setMonthlyPropertyTax("0");
    setZipCode("");
    setState("");
    setTitleFee("");
    setTitleFeeManuallyEdited(false);
    setUnderwritingFee("595");
    setRecordingTax({ amount: 0, name: "Recording Fee", description: "" });
    setProcessingFee(695);
    setProcessingFeeWaived(false);
    setResults(null);
    setError(null);
    setWarnings([]);
  };

  // Auto-fill state when zip code changes
  const handleZipCodeChange = (value) => {
    setZipCode(value);
    if (value.length >= 3) {
      const detectedState = getStateFromZip(value);
      if (detectedState) {
        setState(detectedState);
      }
    }
  };

  const handleCalculate = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        original_closing_date: originalClosingDate || "2020-01-01",
        closing_date: closingDate || new Date().toISOString().split('T')[0],
        original_loan_amount: parseFloat(originalLoanAmount) || 0,
        current_loan_balance: parseFloat(currentLoanBalance) || 0,
        current_interest_rate: parseFloat(currentInterestRate) || 0,
        new_interest_rate: parseFloat(newInterestRate) || 0,
        loan_term_years: parseInt(loanTermYears) || 30,
        discount_points: parseFloat(discountPoints) || 0,
        funding_fee: fundingFee === "yes",
        escrows: parseFloat(titleFee) || 0,
        recording_tax: recordingTax.amount,
        processing_fee: processingFee,
        processing_fee_waived: processingFeeWaived,
        underwriting_fee: parseFloat(underwritingFee) || 595,
        monthly_property_tax: parseFloat(monthlyPropertyTax) || 0,
        state: state || "TX"
      };

      const response = await axios.post(`${API}/calculate`, payload);
      setResults({...response.data, recording_tax_info: recordingTax});
    } catch (err) {
      console.error("Calculation error:", err);
      setError(err.response?.data?.detail || "An error occurred during calculation");
    } finally {
      setLoading(false);
    }
  }, [originalClosingDate, closingDate, originalLoanAmount, currentLoanBalance, 
      currentInterestRate, newInterestRate, loanTermYears, discountPoints, 
      fundingFee, titleFee, recordingTax, processingFee, processingFeeWaived, 
      underwritingFee, monthlyPropertyTax, state]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6 lg:p-8">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-8">
          <p className="section-label mb-2">VA IRRRL</p>
          <h1 className="text-2xl lg:text-3xl font-semibold text-white">Recoup Calculator</h1>
          <p className="text-gray-500 text-sm mt-1">Calculate your refinance savings and recoup time</p>
        </div>

        {/* Input Section */}
        <div className="mb-8">
          <p className="section-label mb-4">LOAN DETAILS</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Original Closing Date */}
            <div className="input-group">
              <label className="input-label">Original Closing Date</label>
              <Input
                type="date"
                data-testid="original-closing-date"
                value={originalClosingDate}
                onChange={(e) => setOriginalClosingDate(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* New Closing Date */}
            <div className="input-group">
              <label className="input-label">New Closing Date</label>
              <Input
                type="date"
                data-testid="closing-date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Original Loan Amount */}
            <div className="input-group">
              <label className="input-label">Original Loan Amount</label>
              <Input
                type="number"
                data-testid="original-loan-amount"
                placeholder="325000"
                value={originalLoanAmount}
                onChange={(e) => setOriginalLoanAmount(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Current Loan Balance */}
            <div className="input-group">
              <label className="input-label">
                Current Loan Balance
              </label>
              <div className="relative">
                <Input
                  type="number"
                  data-testid="current-loan-balance"
                  placeholder="302375.65"
                  value={currentLoanBalance}
                  onChange={(e) => handleBalanceChange(e.target.value)}
                  className="dark-input"
                />
                {!balanceManuallyEdited && currentLoanBalance && (
                  <span className="absolute -top-1 right-0 text-green-500 text-[10px] uppercase tracking-wide">(auto)</span>
                )}
                {balanceManuallyEdited && (
                  <button 
                    onClick={resetBalanceToAuto}
                    className="absolute -top-1 right-0 text-blue-400 text-[10px] uppercase tracking-wide hover:text-blue-300"
                    title="Reset to auto-calculated"
                  >
                    (reset)
                  </button>
                )}
              </div>
            </div>

            {/* Zip Code */}
            <div className="input-group">
              <label className="input-label">Property Zip Code</label>
              <Input
                type="text"
                data-testid="zip-code"
                placeholder="85001"
                maxLength={5}
                value={zipCode}
                onChange={(e) => handleZipCodeChange(e.target.value.replace(/\D/g, ''))}
                className="dark-input"
              />
            </div>

            {/* State */}
            <div className="input-group">
              <label className="input-label">State {state && <span className="text-green-500 text-xs">(auto-filled)</span>}</label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="state-select" className="dark-select-trigger">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="dark-select-content bg-[#111] border-[#222] max-h-[300px]">
                  {US_STATES.map((s) => (
                    <SelectItem key={s.code} value={s.code} className="text-white hover:bg-[#1a1a1a]">
                      {s.code} - {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Interest Rates */}
        <div className="mb-8">
          <p className="section-label mb-4">INTEREST RATES</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Current Interest Rate */}
            <div className="input-group">
              <label className="input-label">Current Rate (%)</label>
              <Input
                type="number"
                step="0.001"
                data-testid="current-interest-rate"
                placeholder="6.875"
                value={currentInterestRate}
                onChange={(e) => setCurrentInterestRate(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* New Interest Rate */}
            <div className="input-group">
              <label className="input-label">New Rate (%)</label>
              <Input
                type="number"
                step="0.001"
                data-testid="new-interest-rate"
                placeholder="4.250"
                value={newInterestRate}
                onChange={(e) => setNewInterestRate(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Discount Points */}
            <div className="input-group">
              <label className="input-label">Discount Points (%)</label>
              <Input
                type="number"
                step="0.125"
                data-testid="discount-points"
                placeholder="2"
                value={discountPoints}
                onChange={(e) => setDiscountPoints(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Loan Term */}
            <div className="input-group">
              <label className="input-label">Loan Term</label>
              <Select value={loanTermYears} onValueChange={setLoanTermYears}>
                <SelectTrigger data-testid="loan-term-years" className="dark-select-trigger">
                  <SelectValue placeholder="30 Years" />
                </SelectTrigger>
                <SelectContent className="dark-select-content bg-[#111] border-[#222]">
                  {LOAN_TERMS.map((term) => (
                    <SelectItem key={term.value} value={term.value} className="text-white hover:bg-[#1a1a1a]">
                      {term.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Funding Fee */}
            <div className="input-group">
              <label className="input-label">Funding Fee (0.5%)</label>
              <Select value={fundingFee} onValueChange={setFundingFee}>
                <SelectTrigger data-testid="funding-fee" className="dark-select-trigger">
                  <SelectValue placeholder="No" />
                </SelectTrigger>
                <SelectContent className="dark-select-content bg-[#111] border-[#222]">
                  <SelectItem value="no" className="text-white hover:bg-[#1a1a1a]">No</SelectItem>
                  <SelectItem value="yes" className="text-white hover:bg-[#1a1a1a]">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Escrow & Fees */}
        <div className="mb-8">
          <p className="section-label mb-4">ESCROW & FEES</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {/* Title Fee (State-based, editable) */}
            <div className="input-group">
              <label className="input-label">Title Fee</label>
              <div className="relative">
                <Input
                  type="number"
                  data-testid="title-fee"
                  placeholder="1500"
                  value={titleFee}
                  onChange={(e) => handleTitleFeeChange(e.target.value)}
                  className="dark-input"
                />
                {!titleFeeManuallyEdited && titleFee && (
                  <span className="absolute -top-1 right-0 text-green-500 text-[10px] uppercase tracking-wide">(auto)</span>
                )}
                {titleFeeManuallyEdited && (
                  <button 
                    onClick={resetTitleFeeToAuto}
                    className="absolute -top-1 right-0 text-blue-400 text-[10px] uppercase tracking-wide hover:text-blue-300"
                    title="Reset to auto-calculated"
                  >
                    (reset)
                  </button>
                )}
              </div>
            </div>

            {/* Recording/Transfer Tax (State-based) */}
            <div className="input-group">
              <label className="input-label" title={recordingTax.description}>
                {recordingTax.name || "Recording Tax"} <span className="text-xs text-gray-500">(Auto)</span>
              </label>
              <div className="metric-card !p-3 flex items-center justify-between">
                <span className={`font-medium ${recordingTax.amount > 500 ? 'text-amber-400' : 'text-white'}`}>
                  {formatCurrency(recordingTax.amount)}
                </span>
                <span className="text-xs text-gray-500">{state || "—"}</span>
              </div>
            </div>

            {/* Monthly Property Tax */}
            <div className="input-group">
              <label className="input-label">Monthly Property Tax</label>
              <Input
                type="number"
                data-testid="monthly-property-tax"
                placeholder="350"
                value={monthlyPropertyTax}
                onChange={(e) => setMonthlyPropertyTax(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Fixed Fees Display */}
            <div className="input-group">
              <label className="input-label">Credit Report Fee</label>
              <div className="metric-card !p-3">
                <span className="text-white font-medium">$125.00</span>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Underwriting Fee</label>
              <Input
                type="number"
                data-testid="underwriting-fee"
                placeholder="595"
                value={underwritingFee}
                onChange={(e) => setUnderwritingFee(e.target.value)}
                className="dark-input"
              />
            </div>

            {/* Processing Fee (Waivable) */}
            <div className="input-group">
              <label className="input-label">Processing Fee</label>
              <div className="metric-card !p-3 flex items-center justify-between">
                <span className={`font-medium ${processingFeeWaived ? 'text-gray-500 line-through' : 'text-white'}`}>
                  $695.00
                </span>
                <button
                  onClick={toggleProcessingFeeWaiver}
                  data-testid="waive-processing-fee"
                  className={`text-xs px-2 py-1 rounded ${
                    processingFeeWaived 
                      ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' 
                      : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                  }`}
                >
                  {processingFeeWaived ? 'Waived' : 'Waive'}
                </button>
              </div>
            </div>

            {/* Calculate Button */}
            <div className="input-group justify-end">
              <label className="input-label opacity-0">Calculate</label>
              <Button 
                onClick={handleCalculate} 
                disabled={loading}
                data-testid="calculate-button"
                className="btn-calculate w-full"
              >
                {loading ? "Calculating..." : "Calculate"}
              </Button>
            </div>
          </div>
        </div>

        {/* VA Eligibility Checks */}
        {(originalClosingDate && closingDate) && (
          <div className="mb-8">
            <p className="section-label mb-4">VA ELIGIBILITY CHECKS</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {/* 210-Day Seasoning */}
              <div className={`metric-card ${meetsSeasoningRequirement ? 'status-pass' : 'status-fail'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {meetsSeasoningRequirement ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <p className="text-xs font-medium uppercase tracking-wide">210-Day Seasoning</p>
                </div>
                <p className="text-lg font-bold">{daysSinceOriginalClosing} days</p>
                <p className="text-xs opacity-70 mt-1">{meetsSeasoningRequirement ? 'Requirement met' : 'Need 210+ days'}</p>
              </div>

              {/* 6 Payment Requirement */}
              <div className={`metric-card ${meetsPaymentRequirement ? 'status-pass' : 'status-fail'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {meetsPaymentRequirement ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <p className="text-xs font-medium uppercase tracking-wide">6 Payments Made</p>
                </div>
                <p className="text-lg font-bold">{paymentsMade} payments</p>
                <p className="text-xs opacity-70 mt-1">{meetsPaymentRequirement ? 'Requirement met' : 'Need 6+ payments'}</p>
              </div>

              {/* Net Tangible Benefit */}
              <div className={`metric-card ${meetsNTBRequirement ? 'status-pass' : (rateReduction > 0 ? 'status-fail' : '')}`}>
                <div className="flex items-center gap-2 mb-2">
                  {meetsNTBRequirement ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : rateReduction > 0 ? (
                    <XCircle className="w-4 h-4 text-red-500" />
                  ) : (
                    <Info className="w-4 h-4 text-gray-400" />
                  )}
                  <p className="text-xs font-medium uppercase tracking-wide">Rate Reduction</p>
                </div>
                <p className={`text-lg font-bold ${meetsNTBRequirement ? 'text-green-500' : rateReduction > 0 ? 'text-red-400' : 'text-white'}`}>
                  {rateReduction > 0 ? `${rateReduction}%` : '—'}
                </p>
                <p className="text-xs opacity-70 mt-1">{meetsNTBRequirement ? 'NTB met (≥0.5%)' : 'Need ≥0.5% reduction'}</p>
              </div>

              {/* Clear Form Button */}
              <div className="input-group justify-end">
                <Button 
                  onClick={handleClearForm}
                  variant="outline"
                  data-testid="clear-form-button"
                  className="w-full h-full bg-transparent border-[#333] text-gray-400 hover:text-white hover:bg-[#1a1a1a]"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear Form
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-500 font-medium text-sm mb-1">Warnings</p>
                <ul className="text-yellow-400/80 text-sm space-y-1">
                  {warnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <div className="animate-fade-in">
            {/* Payment Results */}
            <div className="mb-8">
              <p className="section-label mb-4">PAYMENT COMPARISON</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="metric-card" data-testid="old-payment">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">Old Payment</p>
                    <div className="icon-badge">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="metric-value">{formatCurrency(results.old_payment)}</p>
                </div>

                <div className="metric-card" data-testid="new-payment">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">New Payment</p>
                    <div className="icon-badge">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="metric-value">{formatCurrency(results.new_payment)}</p>
                </div>

                <div className="metric-card" data-testid="monthly-savings">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">Monthly Savings</p>
                    <div className="icon-badge icon-badge-green">
                      <TrendingDown className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="metric-value metric-value-green">{formatCurrency(results.monthly_savings)}</p>
                </div>

                <div className="metric-card" data-testid="lifetime-savings">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">Lifetime Savings</p>
                    <div className="icon-badge icon-badge-green">
                      <Wallet className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="metric-value metric-value-green">{formatCurrency(results.lifetime_interest_savings)}</p>
                </div>

                <div className="metric-card" data-testid="recoup-time">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">Recoup Time</p>
                    <div className="icon-badge">
                      <Clock className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="metric-value">{results.recoup_time_months} <span className="text-sm text-gray-500">months</span></p>
                </div>
              </div>
            </div>

            {/* Recoup Test Status */}
            <div className="mb-8">
              <p className="section-label mb-4">RECOUP TEST STATUS</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className={`metric-card flex items-center gap-4 ${results.passes_recoup_test ? 'status-pass' : 'status-fail'}`} data-testid="recoup-status">
                  {results.passes_recoup_test ? (
                    <>
                      <CheckCircle2 className="w-8 h-8" />
                      <div>
                        <p className="text-lg font-semibold">Passes Recoup Test</p>
                        <p className="text-sm opacity-80">Costs recovered within 36 months</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-8 h-8" />
                      <div>
                        <p className="text-lg font-semibold">Does Not Pass</p>
                        <p className="text-sm opacity-80">Costs exceed 36-month threshold</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="metric-card" data-testid="total-loan-amount">
                  <div className="flex items-center justify-between mb-2">
                    <p className="metric-label">Total Loan Amount</p>
                    <div className="icon-badge">
                      <FileText className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="metric-value">{formatCurrency(results.total_loan_amount)}</p>
                </div>
              </div>
            </div>

            {/* Tax Info */}
            {results.tax_info && parseFloat(monthlyPropertyTax) > 0 && (
              <div className="mb-8">
                <p className="section-label mb-4">TAX ESCROW INFO (AGGREGATE METHOD)</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <div className="metric-card">
                    <p className="metric-label">Tax Schedule</p>
                    <p className="text-white font-medium capitalize">{results.tax_info.schedule}</p>
                  </div>
                  <div className="metric-card">
                    <p className="metric-label">Next Due Date</p>
                    <p className="text-white font-medium">{results.tax_info.next_due_date}</p>
                  </div>
                  <div className="metric-card">
                    <p className="metric-label">Installment Amt</p>
                    <p className="text-white font-medium">{formatCurrency(results.tax_info.installment_amount || 0)}</p>
                  </div>
                  <div className="metric-card">
                    <p className="metric-label">Initial Escrow</p>
                    <p className="text-white font-medium">{results.tax_info.prepaid_months} mo</p>
                  </div>
                  <div className="metric-card">
                    <p className="metric-label">Escrow Deposit</p>
                    <p className="text-white font-medium metric-value-green">{formatCurrency(results.tax_info.prepaid_amount)}</p>
                  </div>
                </div>
                
                {/* Aggregate Analysis Table */}
                {results.tax_info.aggregate_analysis && results.tax_info.aggregate_analysis.length > 0 && (
                  <Collapsible className="mt-4">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-gray-500 hover:text-white w-full justify-between" data-testid="aggregate-toggle">
                        <span className="text-xs uppercase tracking-wide">View Month-by-Month Projection</span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="metric-card mt-2 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#222]">
                              <th className="text-left py-2 px-2 text-gray-500 font-medium">Month</th>
                              <th className="text-right py-2 px-2 text-gray-500 font-medium">Deposit</th>
                              <th className="text-right py-2 px-2 text-gray-500 font-medium">Disbursement</th>
                              <th className="text-right py-2 px-2 text-gray-500 font-medium">Balance*</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.tax_info.aggregate_analysis.map((row, idx) => (
                              <tr key={idx} className="border-b border-[#1a1a1a]">
                                <td className="py-2 px-2 text-white">{row.month}</td>
                                <td className="py-2 px-2 text-right text-gray-400">
                                  {row.deposit > 0 ? `+${formatCurrency(row.deposit)}` : '—'}
                                </td>
                                <td className="py-2 px-2 text-right text-red-400">
                                  {row.disbursement > 0 ? `-${formatCurrency(row.disbursement)}` : '—'}
                                </td>
                                <td className={`py-2 px-2 text-right font-medium ${row.running_total < 0 ? 'text-red-400' : 'text-white'}`}>
                                  {row.running_total >= 0 ? '+' : ''}{formatCurrency(row.running_total)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="text-xs text-gray-600 mt-3">
                          *Balance shown relative to initial deposit. Initial escrow deposit of {formatCurrency(results.tax_info.prepaid_amount)} ensures 2-month cushion at lowest point.
                        </p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            )}

            {/* Cost Breakdown */}
            <Collapsible open={costBreakdownOpen} onOpenChange={setCostBreakdownOpen}>
              <div className="mb-4 flex items-center justify-between">
                <p className="section-label">COST BREAKDOWN</p>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-500 hover:text-white" data-testid="cost-breakdown-toggle">
                    <ChevronDown className={`w-4 h-4 transition-transform ${costBreakdownOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div className="space-y-4" data-testid="cost-breakdown">
                  {/* Lender Fees */}
                  <div className="metric-card">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Lender Fees</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Underwriting Fee</span>
                        <span className="text-white font-medium" data-testid="underwriting-fee">{formatCurrency(results.underwriting_fee)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Credit Report Fee</span>
                        <span className="text-white font-medium" data-testid="credit-report-fee">{formatCurrency(results.credit_report_fee)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm flex items-center gap-2">
                          Processing Fee
                          {results.processing_fee === 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">WAIVED</span>
                          )}
                        </span>
                        <span className={`font-medium ${results.processing_fee === 0 ? 'text-gray-600 line-through' : 'text-white'}`} data-testid="processing-fee">
                          {formatCurrency(results.processing_fee || 695)}
                        </span>
                      </div>
                      {results.points_cost > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Discount Points</span>
                          <span className="text-white font-medium" data-testid="points-cost">{formatCurrency(results.points_cost)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Title & Government Fees */}
                  <div className="metric-card">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Title & Government</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Title Costs</span>
                        <span className="text-white font-medium" data-testid="title-costs">{formatCurrency(results.title_costs)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">{results.recording_tax_info?.name || "Recording Tax"}</span>
                        <span className={`font-medium ${results.recording_tax > 500 ? 'text-amber-400' : 'text-white'}`} data-testid="recording-tax">
                          {formatCurrency(results.recording_tax)}
                        </span>
                      </div>
                      {results.funding_fee_amount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">VA Funding Fee (0.5%)</span>
                          <span className="text-white font-medium" data-testid="funding-fee-amount">{formatCurrency(results.funding_fee_amount)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Escrow / Prepaids */}
                  <div className="metric-card">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Escrow / Prepaids</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">
                          Initial Escrow <span className="text-gray-600">({results.escrow_collection_months} mo)</span>
                        </span>
                        <span className="text-white font-medium" data-testid="initial-escrow">{formatCurrency(results.initial_escrow_collection)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="metric-card !bg-[#0a0a0a] border-green-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-semibold">Total Closing Costs</span>
                      <span className="text-xl font-bold text-green-400" data-testid="total-closing-costs">{formatCurrency(results.total_closing_costs)}</span>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Empty State */}
        {!results && !error && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-[#111] rounded-full flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-400 mb-2">Enter Loan Details</h3>
            <p className="text-gray-600 text-sm max-w-sm mx-auto">
              Fill in your loan information above and click Calculate to see your IRRRL savings analysis
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
